// @ts-nocheck
/**
 * GeoTiffImageOverlay - Image overlay component with error handling and optional masking
 *
 * Renders GeoTIFF layers (RGB, DSM, Flux) as Leaflet ImageOverlay.
 * Supports optional polygon masking via MaskedCanvasOverlay (currently dormant).
 */

import React, { useState, useEffect, useRef } from 'react';
import { ImageOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MaskedCanvasOverlay } from './MaskedCanvasOverlay';

export interface GeoTiffImageOverlayProps {
  url: string;
  bounds: any;
  opacity: number;
  layer: string;
  onLoadingChange?: (status: 'loading' | 'success' | 'error') => void;
  maskPolygons?: [number, number][][] | null; // Array of polygons for masking
}

export function GeoTiffImageOverlay({
  url,
  bounds,
  opacity,
  layer,
  onLoadingChange,
  maskPolygons
}: GeoTiffImageOverlayProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    // Extract cache_id from URL for logging
    const cacheIdMatch = url.match(/cache_id=([a-f0-9]+)/);
    const cacheId = cacheIdMatch ? cacheIdMatch[1] : 'none';

    setImageLoading(true);
    setImageError(false);
    if (onLoadingChange) onLoadingChange('loading');

    // Test if the image loads
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS for screenshot capture
    img.onload = () => {
      setImageLoading(false);
      if (onLoadingChange) onLoadingChange('success');
    };
    img.onerror = (e) => {
      console.error(`❌ [IMAGE OVERLAY] Failed to load ${layer} layer (cache: ${cacheId}):`, e);
      setImageError(true);
      setImageLoading(false);
      if (onLoadingChange) onLoadingChange('error');
    };
    img.src = url;
  }, [url, layer]); // Removed onLoadingChange and maskPolygons to prevent infinite loops

  if (imageError) {
    console.warn(`⚠️ [IMAGE OVERLAY] ${layer} layer failed to load, not rendering overlay`);
    return null;
  }

  if (imageLoading) {
  }

  // If masking is enabled, use canvas-based masked overlay
  if (maskPolygons && maskPolygons.length > 0) {
    return (
      <MaskedCanvasOverlay
        url={url}
        bounds={bounds}
        opacity={opacity}
        layer={layer}
        maskPolygons={maskPolygons}
        onLoadingChange={onLoadingChange}
      />
    );
  }

  // Default: no masking - use custom overlay with CORS support
  return (
    <CrossOriginImageOverlay
      url={url}
      bounds={bounds}
      opacity={opacity}
    />
  );
}

/**
 * Custom ImageOverlay that sets crossOrigin for screenshot capture support
 */
function CrossOriginImageOverlay({ url, bounds, opacity }: { url: string; bounds: any; opacity: number }) {
  const map = useMap();
  const overlayRef = useRef<L.ImageOverlay | null>(null);

  useEffect(() => {
    if (!map || !bounds) return;

    // Create a custom image overlay with crossOrigin
    const overlay = L.imageOverlay(url, bounds, {
      opacity,
      crossOrigin: 'anonymous',
    });

    overlay.addTo(map);
    overlayRef.current = overlay;

    return () => {
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
      }
    };
  }, [map, url, bounds, opacity]);

  // Update opacity if it changes
  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  return null;
}

export default GeoTiffImageOverlay;
